import { useMemo } from 'react';
import { useAppState } from 'lib/app-state';
import { allLocales, availableLocales as compiledLocales } from 'messages';
import type { Locale, LocaleKey } from 'messages';

/**
 * Returns the list of locales available for the language selector.
 *
 * When app settings have been loaded, the list is the intersection of
 * API-available locales and locally compiled translations. Before settings
 * load (or on failure), falls back to all compiled locales.
 */
export function useAvailableLocales(): Locale[] {
  const {
    appSettings
  } = useAppState();
  return useMemo(() => {
    if (!appSettings?.availableLocales?.length) {
      return compiledLocales;
    }
    const compiledKeys = new Set(Object.keys(allLocales) as LocaleKey[]);

    // Build a set of LocaleKeys that the API supports
    const apiKeys = new Set<LocaleKey>();
    for (const lang of appSettings.availableLocales) {
      // Try exact match on culture (e.g. "is-IS")
      if (lang.culture && compiledKeys.has(lang.culture as LocaleKey)) {
        apiKeys.add(lang.culture as LocaleKey);
        continue;
      }
      // Fallback: match by two-letter prefix (e.g. "is" → "is-IS")
      if (lang.twoLetterISOCulture) {
        for (const key of compiledKeys) {
          if (key.split('-')[0]?.toLowerCase() === lang.twoLetterISOCulture.toLowerCase()) {
            apiKeys.add(key);
            break;
          }
        }
      }
    }
    if (apiKeys.size === 0) {
      return compiledLocales;
    }
    return compiledLocales.filter(l => apiKeys.has(l.countryCode));
  }, [appSettings?.availableLocales]);
}